import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { useQueryLog } from './useQueryLog'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

export function useClients(page = 1, pageSize = 100, search = '', sortBy = 'created_at', sortOrder = 'desc', advisorId = null) {
  const { logQuery } = useQueryLog()
  const [clients, setClients] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchClients()
  }, [page, search, sortBy, sortOrder, advisorId])

  const fetchClients = async () => {
    try {
      setLoading(true)
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1

      logQuery('SELECT', 'clients')
      let query = supabase
        .from('clients')
        .select('*, advisors(id, full_name)', { count: 'exact' })

      if (search) {
        query = query.or(`full_name.ilike.%${search}%,surname.ilike.%${search}%,id_number.ilike.%${search}%,email_address.ilike.%${search}%,customer_code.ilike.%${search}%`)
      }

      if (advisorId) {
        query = query.eq('advisor_id', advisorId)
      }

      const { data, count, error: err } = await query
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(from, to)

      if (err) throw err

      // Include advisor info in single query (no N+1)
      let enrichedData = (data || []).map(client => ({
        ...client,
        advisors: client.advisors || null, // Pre-populated by Supabase if using join
      }))

      // Fetch policy counts in memory if needed
      // TODO: In future, use Supabase counts view instead of separate query
      if (enrichedData.length > 0) {
        logQuery('SELECT', 'policies')
        const clientIds = enrichedData.map(c => c.id)
        // Fetch ALL policy client_ids for counting (batched to avoid URL limits)
        const countMap = {}
        for (let i = 0; i < clientIds.length; i += 100) {
          const batch = clientIds.slice(i, i + 100)
          const { data: policyRows } = await supabase
            .from('policies')
            .select('client_id', { count: 'exact', head: false })
            .in('client_id', batch)
          ;(policyRows || []).forEach(p => {
            countMap[p.client_id] = (countMap[p.client_id] || 0) + 1
          })
        }
        enrichedData = enrichedData.map(c => ({ ...c, policy_count: countMap[c.id] || 0 }))
      }

      setClients(enrichedData)
      setTotal(count || 0)
    } catch (err) {
      console.error('fetchClients error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const createClient = async (clientData, addressData, maritalData, bankingData, relativesData) => {
    // Clean data: convert empty strings to null for UUID fields
    const cleanData = { ...clientData }
    if (cleanData.entity_id === '') cleanData.entity_id = null
    if (cleanData.advisor_id === '') cleanData.advisor_id = null
    if (cleanData.date_of_birth === '') cleanData.date_of_birth = null
    if (cleanData.retirement_date === '') cleanData.retirement_date = null

    // Insert client
    logQuery('INSERT', 'clients')
    const { data: clientInsert, error: clientError } = await supabase
      .from('clients')
      .insert([cleanData])
      .select()

    if (clientError) throw clientError

    const clientId = clientInsert[0].id

    // Insert address if provided
    if (addressData && Object.values(addressData).some(val => val)) {
      const { error: addressError } = await supabase
        .from('client_addresses')
        .insert([{ ...addressData, client_id: clientId }])
      if (addressError) throw addressError
    }

    // Insert marital details if provided
    if (maritalData && Object.values(maritalData).some(val => val)) {
      const { error: maritalError } = await supabase
        .from('client_marital_details')
        .insert([{ ...maritalData, client_id: clientId }])
      if (maritalError) throw maritalError
    }

    // Insert banking details if provided
    if (bankingData && Object.values(bankingData).some(val => val)) {
      const { error: bankingError } = await supabase
        .from('client_banking_details')
        .insert([{ ...bankingData, client_id: clientId }])
      if (bankingError) throw bankingError
    }

    // Insert relatives if provided
    if (relativesData && relativesData.length > 0) {
      const relativesWithClientId = relativesData.map(rel => ({ ...rel, client_id: clientId }))
      const { error: relativeError } = await supabase
        .from('client_relatives')
        .insert(relativesWithClientId)
      if (relativeError) throw relativeError
    }

    await fetchClients()
    return clientInsert[0]
  }

  const toggleActive = async (id, currentValue) => {
    setClients(prev => prev.map(c => c.id === id ? { ...c, active: !currentValue } : c))
    const { error } = await supabase
      .from('clients')
      .update({ active: !currentValue })
      .eq('id', id)
    if (error) {
      setClients(prev => prev.map(c => c.id === id ? { ...c, active: currentValue } : c))
      console.error('toggleActive error:', error)
    }
  }

  const CLIENT_FIELDS = [
    'entity_id', 'advisor_id', 'full_name', 'surname', 'initials', 'preferred_name',
    'title', 'gender', 'id_number', 'customer_code', 'tax_number', 'mobile_number', 'email_address',
    'client_status', 'date_of_birth', 'retirement_date', 'active',
  ]

  const updateClient = async (id, clientData, addressData, maritalData, bankingData, relativesData) => {
    // Strip non-column fields (joined objects, etc.)
    const clean = {}
    CLIENT_FIELDS.forEach(f => { if (clientData[f] !== undefined) clean[f] = clientData[f] || null })
    if (clean.entity_id === '') clean.entity_id = null
    if (clean.advisor_id === '') clean.advisor_id = null
    if (clean.date_of_birth === '') clean.date_of_birth = null
    if (clean.retirement_date === '') clean.retirement_date = null

    const { error: clientError } = await supabase.from('clients').update(clean).eq('id', id)
    if (clientError) throw clientError

    // Address — upsert
    const { data: existingAddr } = await supabase.from('client_addresses').select('id').eq('client_id', id).maybeSingle()
    if (existingAddr) {
      await supabase.from('client_addresses').update(addressData).eq('client_id', id)
    } else if (addressData && Object.values(addressData).some(v => v)) {
      await supabase.from('client_addresses').insert([{ ...addressData, client_id: id }])
    }

    // Marital — upsert
    const { data: existingMarital } = await supabase.from('client_marital_details').select('id').eq('client_id', id).maybeSingle()
    if (existingMarital) {
      await supabase.from('client_marital_details').update(maritalData).eq('client_id', id)
    } else if (maritalData && Object.values(maritalData).some(v => v)) {
      await supabase.from('client_marital_details').insert([{ ...maritalData, client_id: id }])
    }

    // Banking — upsert
    const { data: existingBanking } = await supabase.from('client_banking_details').select('id').eq('client_id', id).maybeSingle()
    if (existingBanking) {
      await supabase.from('client_banking_details').update(bankingData).eq('client_id', id)
    } else if (bankingData && Object.values(bankingData).some(v => v)) {
      await supabase.from('client_banking_details').insert([{ ...bankingData, client_id: id }])
    }

    // Relatives — replace
    await supabase.from('client_relatives').delete().eq('client_id', id)
    if (relativesData?.length > 0) {
      const rows = relativesData.map(({ id: _id, client_id: _cid, ...rest }) => ({ ...rest, client_id: id }))
      await supabase.from('client_relatives').insert(rows)
    }

    await fetchClients()
  }

  const getClientById = async (id) => {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        entities(id, name, type, entity_num),
        advisors(id, full_name, email),
        client_addresses(*),
        client_marital_details(*),
        client_banking_details(*),
        client_attachments(*),
        client_relatives(*)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data
  }

  // Returns { created: number, updated: number, relinked: number }
  // Upsert priority: customer_code (if present) → full_name fallback
  // onProgress(done, total) called after each row processed
  const importClients = async (clientsData, { replaceAll = false, onProgress } = {}) => {
    const CHUNK = 250

    if (replaceAll) {
      const incomingCodes = clientsData.map(r => r.customer_code).filter(Boolean)

      // 1. Delete clients with no customer_code (orphan cleanup)
      logQuery('DELETE', 'clients')
      await supabase.from('clients').delete().is('customer_code', null)

      // 2. Delete existing clients whose codes appear in the import file
      //    (done in chunks of 100 to avoid URL length limits)
      for (let i = 0; i < incomingCodes.length; i += 100) {
        const codeChunk = incomingCodes.slice(i, i + 100)
        logQuery('DELETE', 'clients')
        const { error } = await supabase
          .from('clients')
          .delete()
          .in('customer_code', codeChunk)
        if (error) throw error
      }

      // 3. Insert all incoming rows fresh in chunks of 250
      let created = 0
      for (let i = 0; i < clientsData.length; i += CHUNK) {
        const chunk = clientsData.slice(i, i + CHUNK)
        logQuery('INSERT', 'clients')
        const { error } = await supabase.from('clients').insert(chunk)
        if (error) throw error
        created += chunk.length
        onProgress?.(created, clientsData.length)
      }

      const relinked = await relinkPoliciesByCode()
      await fetchClients()
      return { created, updated: 0, relinked }
    }

    // --- Normal import: upsert against all existing records ---
    logQuery('SELECT', 'clients')

    // Fetch all existing clients for matching
    const existingByCode = {}
    const existingByName = {}
    let offset = 0
    while (true) {
      const { data } = await supabase
        .from('clients')
        .select('id, customer_code, full_name')
        .range(offset, offset + 999)
      if (!data?.length) break
      data.forEach(c => {
        if (c.customer_code) existingByCode[c.customer_code.toLowerCase()] = c
        if (c.full_name)     existingByName[c.full_name.toLowerCase()] = c
      })
      if (data.length < 1000) break
      offset += 1000
    }

    // Convert empty strings to null so they don't violate UNIQUE constraints
    const clean = (obj) => Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v === '' ? null : v])
    )

    let created = 0, updated = 0, processed = 0
    for (const row of clientsData) {
      const cleanRow = clean(row)
      const existing =
        (cleanRow.customer_code && existingByCode[cleanRow.customer_code.toLowerCase()]) ||
        (cleanRow.full_name && existingByName[cleanRow.full_name.toLowerCase()])

      if (existing) {
        logQuery('UPDATE', 'clients')
        const { error } = await supabase.from('clients').update(cleanRow).eq('id', existing.id)
        if (error) throw error
        updated++
      } else {
        logQuery('INSERT', 'clients')
        const { error } = await supabase.from('clients').insert([cleanRow])
        if (error) throw error
        created++
      }
      processed++
      onProgress?.(processed, clientsData.length)
    }

    const relinked = await relinkPoliciesByCode()
    await fetchClients()
    return { created, updated, relinked }
  }

  // Relink policies → clients by matching customer_code on both tables
  const relinkPoliciesByCode = async () => {
    // Fetch all clients that have a customer_code
    const allClients = []
    let offset = 0
    while (true) {
      const { data } = await supabase
        .from('clients')
        .select('id, customer_code')
        .not('customer_code', 'is', null)
        .range(offset, offset + 999)
      if (!data?.length) break
      allClients.push(...data)
      if (data.length < 1000) break
      offset += 1000
    }

    // Update policies in parallel batches of 20
    const BATCH = 20
    let relinked = 0
    for (let i = 0; i < allClients.length; i += BATCH) {
      const results = await Promise.all(
        allClients.slice(i, i + BATCH).map(c =>
          supabase
            .from('policies')
            .update({ client_id: c.id })
            .eq('customer_code', c.customer_code)
        )
      )
      relinked += results.filter(r => !r.error).length
    }
    return relinked
  }

  const bulkAssignAdvisor = async (advisorId, clientIds) => {
    const { error } = await supabase
      .from('clients')
      .update({ advisor_id: advisorId })
      .in('id', clientIds)
    if (error) throw error
    await fetchClients()
  }

  return {
    clients,
    total,
    loading,
    error,
    createClient,
    updateClient,
    toggleActive,
    getClientById,
    importClients,
    relinkPoliciesByCode,
    bulkAssignAdvisor,
    refetch: fetchClients
  }
}
